import {
  lineChartOffsetter,
  pluginDisplayer,
  pluginFont,
} from '@/chart/plugins/display-min-max-value-plugin';
import { formatUnit, numberToAbbreviatedNumber } from '@/utils/string-utils';
import { tooltipLabelCB } from './chart-helpers';

// Variables that have set default values
const tickFontColor = 'rgba(0,0,0,0.5)';
const yMaxTicksLimit = 6;
const yBorderDash = [4, 6];
const defaultLabelGridSpacing = 17;
const straightLine = { elements: { line: { tension: 0 } } };

/**
 * Base chart options class. Each value has a default value that is based on the most common case
 * for charts in our app. If the most common case changes, the defaults should be updated.
 *
 * @class ChartOptions
 */
class ChartOptions {
  constructor(options) {
    this.responsive = options.responsive || true; // Resize on chart container size change
    this.maintainAspectRatio = options.maintainAspectRatio || true; // maintain pixel aspect ratio
    this.legend = options.legend || false; // whether to show legend or not
    this.straightLines = options.straightLine || true; // line charts should be straight by default
    // inner padding for charts. Axis included.
    this.padding = options.padding || { top: 20, right: 5 };
    this.disableAxis = options.disableAxis || false; // Hide all axis
    this.disableXAxis = options.disableXAxis || false; // hide just X axis
    // apply half tick offset. Moves chart over half tick
    this.applyOffset = options.applyOffset || false;
    // Separates the X axis labels from the axis.
    this.spaceBetweenGridAndXAxis =
      options.spaceBetweenGridAndXAxis || defaultLabelGridSpacing;
    // X axis grid lines are vertical |
    this.xGridLines = {
      drawBorder: false, // Actual axis
      display: false, // axis grid
    };
    // Ticks are the labels
    this.xTicks = {
      fontColor: tickFontColor,
      display: !this.disableXAxis,
      padding: this.spaceBetweenGridAndXAxis,
      fontFamily: "'Nunito Sans',sans-serif",
    };
    // Y Grid lines are horizontal ----
    this.yGridLines = {
      display: !this.disableAxis,
      drawBorder: false,
      zeroLineBorderDash: yBorderDash,
      zeroLineColor: 'rgba(0, 0, 0, 0.1)',
      borderDash: yBorderDash, // [4, 6] means: 4px dash, 6px space
    };

    // Y axis labels
    this.yTicks = {
      fontColor: tickFontColor,
      display: !this.disableAxis,
      maxTicksLimit: yMaxTicksLimit,
      fontSize: 10,
      fontFamily: "'Nunito Sans',sans-serif",
      /**
       * This callback function is called for every yTick label to determine the disaply value.
       *
       * @param {number} v The raw value to display
       * @param {number} i The index of the value within the data collection
       * @param {number[]} a The entire data collection
       * @returns {string}
       */
      callback: (v, i, a) => {
        if (!!options?.yTicksFormatter) {
          return options.yTicksFormatter(v, i, a);
        }

        // NOTE: We may want to supply more configuration options here to control wether all/none/first/last items are
        // formatted with units displayed. At the moment this is fixed to the first element only.
        if (i === 0) {
          return formatUnit(v, options?.tooltipParams?.unit, true);
        }

        return numberToAbbreviatedNumber(v);
      },
    };
  }

  get straightLine() {
    const straightLineOption = this.straightLines ? straightLine : {};
    return straightLineOption;
  }

  get legendOption() {
    const legend = { display: this.legend };
    return legend;
  }

  get layout() {
    const layout = {
      padding: this.padding,
    };
    return layout;
  }

  get offset() {
    const offset = { offset: this.applyOffset };
    return offset;
  }

  get xAxes() {
    const xAxes = [
      { gridLines: this.xGridLines, ticks: this.xTicks, ...this.offset },
    ];
    return xAxes;
  }

  get yAxes() {
    const yAxes = [{ gridLines: this.yGridLines, ticks: this.yTicks }];
    return yAxes;
  }

  get chartScales() {
    return {
      xAxes: this.xAxes,
      yAxes: this.yAxes,
    };
  }

  // Collects all options into single object
  get chartOptions() {
    return {
      responsive: this.responsive,
      ...this.straightLine,
      maintainAspectRatio: this.maintainAspectRatio,
      legend: this.legendOption,
      scales: this.chartScales,
      tooltips: this.toolTips,
      plugins: this.plugins,
      layout: this.layout,
    };
  }
}

/**
 * Unique options related to line charts
 *
 * @export
 * @class LineChartOptions
 * @extends {ChartOptions}
 */
export class LineChartOptions extends ChartOptions {
  constructor(options = {}) {
    super(options);

    this.pluginParams = options.pluginParams;
    this.tooltips = options.tooltips || true; // toggle all tooltips on/off
    // The custom tooltip is currently used by chart-in-kpi-card
    this.customTooltip = options.customTooltip;
    this.tooltipParams = options.tooltipParams;
    // toggle all plugins on/off
    this.enablePlugins = options.enablePlugins || false;
  }

  get toolTips() {
    // default tooltips
    const tooltips = {
      mode: 'nearest',
      intersect: false,
      position: 'nearest',
      displayColors: true,
      callbacks: {
        label: (tooltipItem) =>
          tooltipLabelCB(tooltipItem, this.tooltipParams.unit),
      },
    };
    if (this.tooltips) {
      return this.customTooltip || tooltips;
    }
    return {};
  }

  get plugins() {
    // line chart plugin options
    const pluginOptions = {
      datalabels: {
        color: this.pluginParams.pluginTextColor,
        align: this.pluginParams.pluginAlignment,
        // allows for overriding label text
        formatter: this.pluginParams.pluginFormatter,
        font: pluginFont,
        offset: (ctx) => lineChartOffsetter(ctx),
        display: (ctx) => pluginDisplayer(ctx),
      },
    };
    if (this.enablePlugins) {
      return pluginOptions;
    }
    return {};
  }
}

/**
 * Unique options for bar charts
 *
 * @export
 * @class BarChartOptions
 * @extends {ChartOptions}
 */
export class BarChartOptions extends ChartOptions {
  constructor(options = {}) {
    super({
      ...options,
      applyOffset: options?.applyOffset ?? true,
    });
    this.pluginParams = options.pluginParams;
    this.tooltips = options.tooltips || true;
    this.tooltipParams = options.tooltipParams;
    this.chartPlugins = options.plugins || true;
    // Stacks all datasets on each data point
    this.isStackedBar = options.isStackedBar || false;
    // Bar chart has all the ytick options + it starts at zero
    this.yTicksBarChart = {
      ...this.yTicks,
      beginAtZero: true,
    };
  }

  // overrides base class to feed stacked option
  get xAxes() {
    const xAxes = [
      {
        gridLines: this.xGridLines,
        ticks: this.xTicks,
        ...this.offset,
        stacked: this.isStackedBar,
      },
    ];
    return xAxes;
  }

  // overrides base class to feed stacked option
  get yAxes() {
    const yAxes = [
      {
        gridLines: this.yGridLines,
        ticks: this.yTicksBarChart,
        stacked: this.isStackedBar,
      },
    ];
    return yAxes;
  }

  get toolTips() {
    const tooltips = {
      displayColors: true,
      callbacks: {
        label: (tooltipItem) =>
          tooltipLabelCB(tooltipItem, this.tooltipParams.unit),
      },
    };
    if (this.tooltips) {
      return tooltips;
    }
    return {};
  }

  get plugins() {
    const pluginOptions = {
      datalabels: {
        color: this.pluginParams.pluginTextColor,
        align: this.pluginParams.pluginAlignment,
        anchor: 'end',
        formatter: this.pluginParams.pluginFormatter,
        font: pluginFont,
        offset: 2,
        display: (ctx) => pluginDisplayer(ctx),
      },
    };
    if (this.chartPlugins) {
      return pluginOptions;
    }
    return {};
  }
}
